import { createImmerReducer } from "utils/ReducerUtils";
import type { ReduxAction } from "ee/constants/ReduxActionConstants";
import { ReduxActionTypes } from "ee/constants/ReduxActionConstants";
import type {
  ModuleInstance,
  ModuleInstanceId,
  QueryModuleInstance,
} from "ee/constants/ModuleInstanceConstants";
import type {
  BulkAddRemoveModuleInstanceActionPayload,
  ConvertEntityToInstanceResponse,
  CreateModuleInstanceResponse,
} from "ee/api/ModuleInstanceApi";
import { klona } from "klona";
import type { DummyModuleInstanceResponse } from "ee/actions/moduleInstanceActions";
import type { Module } from "ee/constants/ModuleConstants";

export type ModuleInstanceReducerState = Record<
  ModuleInstanceId,
  ModuleInstance
>;

const initialState: ModuleInstanceReducerState = {};

export const handlers = {
  [ReduxActionTypes.CREATE_MODULE_INSTANCE_SUCCESS]: (
    draftState: ModuleInstanceReducerState,
    action: ReduxAction<CreateModuleInstanceResponse>,
  ) => {
    const { moduleInstance } = action.payload;
    const { sourceModuleId } = moduleInstance;
    const dummyModuleInstance = Object.values(draftState).find(
      (mi) => mi.sourceModuleId === sourceModuleId && mi.isDummy,
    );

    if (dummyModuleInstance) {
      delete draftState[dummyModuleInstance.id];
    }

    draftState[moduleInstance.id] = moduleInstance;
    return draftState;
  },

  [ReduxActionTypes.MODIFY_MODULE_REFERENCE_BY_NAME_SUCCESS]: (
    draftState: ModuleInstanceReducerState,
    action: ReduxAction<BulkAddRemoveModuleInstanceActionPayload>,
  ) => {
    const { add, remove } = action.payload.data;

    add.forEach(({ moduleInstance }) => {
      const { sourceModuleId } = moduleInstance;
      const dummyModuleInstance = Object.values(draftState).find(
        (mi) => mi.sourceModuleId === sourceModuleId && mi.isDummy,
      );

      if (dummyModuleInstance) {
        delete draftState[dummyModuleInstance.id];
      }

      draftState[moduleInstance.id] = moduleInstance;
    });

    remove.forEach(({ id }) => {
      delete draftState[id];
    });
  },

  [ReduxActionTypes.UPDATE_MODULE_INSTANCE_SUCCESS]: (
    draftState: ModuleInstanceReducerState,
    action: ReduxAction<QueryModuleInstance>,
  ) => {
    draftState[action.payload.id] = action.payload;
    return draftState;
  },

  [ReduxActionTypes.DELETE_MODULE_INSTANCE_SUCCESS]: (
    draftState: ModuleInstanceReducerState,
    action: ReduxAction<QueryModuleInstance>,
  ) => {
    delete draftState[action.payload.id];
    return draftState;
  },

  [ReduxActionTypes.FETCH_MODULE_INSTANCE_FOR_PAGE_SUCCESS]: (
    draftState: ModuleInstanceReducerState,
    action: ReduxAction<QueryModuleInstance[]>,
  ) => {
    draftState = klona(initialState);
    const moduleInstances = action.payload;

    moduleInstances.forEach((moduleInstance: QueryModuleInstance) => {
      draftState[moduleInstance.id] = moduleInstance;
    });

    return draftState;
  },

  [ReduxActionTypes.GENERATE_DUMMY_MODULE_INSTANCES_SUCCESS]: (
    draftState: ModuleInstanceReducerState,
    action: ReduxAction<DummyModuleInstanceResponse>,
  ) => {
    const { dummyModuleInstances } = action.payload;

    dummyModuleInstances.forEach((moduleInstance) => {
      draftState[moduleInstance.id] = moduleInstance;
    });
  },

  [ReduxActionTypes.GENERATE_DUMMY_MODULE_INSTANCES_INIT]: (
    draftState: ModuleInstanceReducerState,
  ) => {
    const dummyModuleInstanceIds = Object.values(draftState).map((mi) => {
      return mi.isDummy ? mi.id : null;
    });

    dummyModuleInstanceIds.forEach((id) => {
      if (id) {
        delete draftState[id];
      }
    });
  },

  [ReduxActionTypes.SAVE_MODULE_INSTANCE_NAME_SUCCESS]: (
    draftState: ModuleInstanceReducerState,
    action: ReduxAction<{
      id: ModuleInstanceId;
      newName: string;
    }>,
  ) => {
    const { id, newName } = action.payload;

    draftState[id].name = newName;

    return draftState;
  },

  [ReduxActionTypes.RESET_EDITOR_REQUEST]: () => {
    return klona(initialState);
  },

  [ReduxActionTypes.CONVERT_ENTITY_TO_INSTANCE_SUCCESS]: (
    draftState: ModuleInstanceReducerState,
    action: ReduxAction<ConvertEntityToInstanceResponse>,
  ) => {
    const { moduleInstanceData: { moduleInstance } = {} } = action.payload;

    if (moduleInstance) {
      draftState[moduleInstance.id] = moduleInstance;
    }
  },

  [ReduxActionTypes.COPY_MODULE_INSTANCE_SUCCESS]: (
    draftState: ModuleInstanceReducerState,
    action: ReduxAction<CreateModuleInstanceResponse>,
  ) => {
    const { moduleInstance } = action.payload;
    draftState[moduleInstance.id] = moduleInstance;
    return draftState;
  },

  [ReduxActionTypes.SAVE_MODULE_NAME_SUCCESS]: (
    draftState: ModuleInstanceReducerState,
    action: ReduxAction<Module>,
  ) => {
    const { id, name } = action.payload;

    Object.keys(draftState).forEach((moduleInstanceId) => {
      const moduleInstance = draftState[moduleInstanceId];

      if (moduleInstance.sourceModuleId === id && moduleInstance.isDummy) {
        moduleInstance.name = name;
      }
    });
  },
};

const moduleInstanceReducer = createImmerReducer(initialState, handlers);

export default moduleInstanceReducer;
